import { useEffect, useState, type ChangeEvent, type FormEvent } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Eye, ImageIcon, MapPin, Phone } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { InlineInput } from "@/components/ui/inline-input"
import { Label } from "@/components/ui/label"
import { LocationSectionScreen } from "@/features/staff/location-section-screen"
import { t } from "@/lib/i18n"
import { patchInline } from "@/lib/inline-update"

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

/**
 * Only what `locations#update` writes. The feature switches, the slug, the tips
 * and the taxes each have their own screen and their own endpoint, so none of
 * them reach this form.
 */
type LocationProfile = {
  name: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
  latitude: string | null
  longitude: string | null
  logoUrl: string | null
  fullAddress: string | null
  supportPhone: string | null
  // Soft requirement (decision 12): shown here, never blocking publishing.
  // Backs the public menu's meta description and JSON-LD description.
  description: string | null
}

type Props = {
  location: LocationProfile
  errors?: Record<string, string | string[]>
}

function LogoUpload({ logoUrl }: { logoUrl: string | null }) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const { setData, post, processing, errors } = useForm<{ location: { logo: File | null }; _method: string }>({
    location: { logo: null },
    _method: "patch",
  })

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const imageSrc = previewUrl ?? logoUrl

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    setData("location", { logo: file })
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(file ? URL.createObjectURL(file) : null)
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    // Inertia sends a multipart POST; the `_method: patch` field makes Rack read it back as PATCH.
    post("/manage/location", {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => {
        toast.success(tSection("logo_updated"))
        setPreviewUrl(null)
      },
    })
  }

  const errorBag = errors as Record<string, string | undefined>

  return (
    <form onSubmit={handleSubmit} className="space-y-2" data-testid="location-logo-form">
      <Label htmlFor="location-logo">{tSection("logo")}</Label>
      <div className="flex items-center gap-4">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={tSection("logo")}
            className="size-20 rounded-lg border object-cover"
            data-testid="location-logo-preview"
          />
        ) : (
          <div className="flex size-20 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
            <ImageIcon className="size-5" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <Input
            id="location-logo"
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            onChange={handleChange}
            data-testid="location-logo-input"
          />
          <Button type="submit" size="sm" variant="outline" disabled={processing} data-testid="location-logo-save">
            {tSection("save_logo")}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{tSection("logo_hint")}</p>
      {errorBag.logo && <p className="text-sm font-medium text-destructive">{errorBag.logo}</p>}
    </form>
  )
}


export default function StaffLocationManageLocationEdit() {
  const { location } = usePage<Props>().props

  // Light per-field commit, which is what these fields are: a manager fixes a
  // phone number or a postal code, one at a time, months apart. The screens
  // that configure something in one transaction — tips, taxes — keep their
  // explicit submit. See .interface-design/system.md.
  const patchField = (field: string, value: string) =>
    patchInline("/manage/location", { location: { [field]: value } })

  return (
    <LocationSectionScreen
      title={tSection("directory_profile_title")}
      description={tSection("directory_profile_description")}
    >
      {/* What these fields produce. Nobody fills in a latitude without knowing
          what it is for, and the diner-facing sentence is the answer. Paper
          surface rather than another white card: the tonal step says "this is
          the public side" without a label saying so. */}
      <div className="space-y-2">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Eye className="size-3.5" />
          {tSection("preview_label")}
        </p>
        <div className="flex items-start gap-4 rounded-xl border bg-background p-4" data-testid="location-public-preview">
          {location.logoUrl ? (
            <img src={location.logoUrl} alt={location.name} className="size-14 shrink-0 rounded-lg border object-cover" />
          ) : (
            <div className="flex size-14 shrink-0 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
              <ImageIcon className="size-5" />
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-1">
            <p className="truncate font-semibold">{location.name}</p>
            {location.description ? (
              <p className="text-sm text-muted-foreground">{location.description}</p>
            ) : (
              <p className="text-sm text-muted-foreground/60">{tSection("preview_no_description")}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-muted-foreground">
              {location.fullAddress && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="size-3.5" />
                  {location.fullAddress}
                </span>
              )}
              {location.supportPhone && (
                <span className="flex items-center gap-1.5 tabular-nums">
                  <Phone className="size-3.5" />
                  {location.supportPhone}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-6">
          <LogoUpload logoUrl={location.logoUrl} />

          <div className="space-y-2">
            <Label htmlFor="description">{tSection("description")}</Label>
            <InlineInput
              id="description"
              data-testid="location-description-input"
              value={location.description || ""}
              placeholder={tSection("description_placeholder")}
              onSubmit={(value) => patchField("description", value)}
            />
            <p className="text-xs text-muted-foreground">{tSection("description_hint")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line1">{tSection("address_line1")}</Label>
            <InlineInput
              id="address_line1"
              data-testid="location-address-line1-input"
              value={location.addressLine1 || ""}
              placeholder={tSection("address_line1_placeholder")}
              onSubmit={(value) => patchField("address_line1", value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address_line2">{tSection("address_line2")}</Label>
            <InlineInput
              id="address_line2"
              data-testid="location-address-line2-input"
              value={location.addressLine2 || ""}
              placeholder={tSection("address_line2_placeholder")}
              onSubmit={(value) => patchField("address_line2", value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="city">{tSection("city")}</Label>
              <InlineInput
                id="city"
                data-testid="location-city-input"
                value={location.city || ""}
                placeholder={tSection("city_placeholder")}
                onSubmit={(value) => patchField("city", value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">{tSection("state")}</Label>
              <InlineInput
                id="state"
                data-testid="location-state-input"
                value={location.state || ""}
                placeholder={tSection("state_placeholder")}
                onSubmit={(value) => patchField("state", value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_code">{tSection("postal_code")}</Label>
              <InlineInput
                id="postal_code"
                data-testid="location-postal-code-input"
                value={location.postalCode || ""}
                placeholder={tSection("postal_code_placeholder")}
                onSubmit={(value) => patchField("postal_code", value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">{tSection("country")}</Label>
              <InlineInput
                id="country"
                data-testid="location-country-input"
                value={location.country || ""}
                placeholder={tSection("country_placeholder")}
                onSubmit={(value) => patchField("country", value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{tSection("coordinates")}</Label>
            <div className="grid gap-4 sm:grid-cols-2">
              <InlineInput
                id="latitude"
                data-testid="location-latitude-input"
                value={location.latitude || ""}
                placeholder={tSection("latitude")}
                onSubmit={(value) => patchField("latitude", value)}
              />
              <InlineInput
                id="longitude"
                data-testid="location-longitude-input"
                value={location.longitude || ""}
                placeholder={tSection("longitude")}
                onSubmit={(value) => patchField("longitude", value)}
              />
            </div>
            <p className="text-xs text-muted-foreground">{tSection("coordinates_hint")}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="support_phone">{tSection("support_phone")}</Label>
            <InlineInput
              id="support_phone"
              data-testid="location-support-phone-input"
              value={location.supportPhone || ""}
              placeholder={tSection("support_phone_placeholder")}
              onSubmit={(value) => patchField("support_phone", value)}
            />
            <p className="text-xs text-muted-foreground">{tSection("support_phone_hint")}</p>
          </div>
        </CardContent>
      </Card>
    </LocationSectionScreen>
  )
}
